import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { X, Mail, Linkedin, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import Seo from '../components/Seo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MEMBER_COLORS = [
  'bg-[#D6E4EC] text-[#3A6B8C]',
  'bg-[#E8DDD3] text-[#8B6E4E]',
  'bg-[#DDE8D6] text-[#5A7A4A]',
  'bg-[#E8D6E4] text-[#7A4A6B]',
  'bg-[#ECE4D6] text-[#8C7A3A]',
  'bg-[#D6ECEC] text-[#3A8C8C]',
  'bg-[#ECD6D6] text-[#8C3A3A]',
  'bg-[#D6D6EC] text-[#3A3A8C]',
];

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const FALLBACK_ABOUT = [
  'twoplustwo was founded with a simple belief: that architecture should serve both people and planet. based in ahmedabad, we have spent years crafting spaces that respond to the unique cultural and environmental context of india.',
  'our name reflects our collaborative approach — two minds plus two more, always growing, always learning. we believe the best design emerges from dialogue: between architect and client, between building and landscape, between tradition and innovation.',
  'every project we undertake is an opportunity to explore these conversations and create something meaningful.'
];

const FALLBACK_VALUES = [
  { title: 'sangam', subtitle: 'confluence', description: 'we believe in the meeting of ideas — where traditional wisdom flows into contemporary practice, creating spaces that honor both.' },
  { title: 'sthira', subtitle: 'stability', description: 'our designs are rooted in permanence and purpose, built to endure through time while adapting to the lives within them.' },
  { title: 'sukha', subtitle: 'comfort', description: 'beyond aesthetics, we design for wellbeing — spaces that nurture the body, calm the mind, and lift the spirit.' }
];

/* ── Apple-style full-width slideshow ── */
const GallerySlideshow = ({ images, heading }) => {
  const [current, setCurrent] = useState(0);
  const timerRef = useRef(null);
  const count = images.length;

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrent(prev => (prev + 1) % count);
    }, 5000);
  }, [count]);

  useEffect(() => {
    resetTimer();
    return () => clearInterval(timerRef.current);
  }, [resetTimer]);

  const go = (dir) => {
    setCurrent(prev => (prev + dir + count) % count);
    resetTimer();
  };

  return (
    <section className="relative w-screen left-1/2 -translate-x-1/2" data-testid="studio-gallery" style={{ height: '70vh' }}>
      {heading && (
        <h2 className="absolute top-6 left-0 right-0 z-10 text-center text-2xl text-white lowercase"
          style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, textShadow: '0 2px 12px rgba(0,0,0,.45)' }}>
          {heading}
        </h2>
      )}
      <div className="relative w-full h-full overflow-hidden">
        <div className="flex h-full" style={{
          transform: `translateX(-${current * 100}%)`,
          transition: 'transform 0.8s cubic-bezier(0.77, 0, 0.175, 1)'
        }}>
          {images.map((src, i) => (
            <div key={i} className="w-full h-full flex-shrink-0">
              <img src={src} alt={`Studio ${i + 1}`} className="w-full h-full object-cover" draggable={false} />
            </div>
          ))}
        </div>
      </div>
      {count > 1 && (
        <>
          <button onClick={() => go(-1)} aria-label="Previous"
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-2 text-white/70 hover:text-white transition-colors"
            data-testid="gallery-prev">
            <ChevronLeft size={32} strokeWidth={1.5} />
          </button>
          <button onClick={() => go(1)} aria-label="Next"
            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-2 text-white/70 hover:text-white transition-colors"
            data-testid="gallery-next">
            <ChevronRight size={32} strokeWidth={1.5} />
          </button>
        </>
      )}
      {count > 1 && (
        <div className="absolute bottom-5 left-0 right-0 flex justify-center gap-2 z-10" data-testid="gallery-dots">
          {images.map((_, i) => (
            <button key={i} onClick={() => { setCurrent(i); resetTimer(); }}
              aria-label={`Slide ${i + 1}`}
              className={`rounded-full transition-all duration-300 ${i === current ? 'w-6 h-2 bg-white' : 'w-2 h-2 bg-white/50 hover:bg-white/80'}`} />
          ))}
        </div>
      )}
    </section>
  );
};

const Studio = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [aboutExpanded, setAboutExpanded] = useState(false);

  useEffect(() => { loadAllContent(); }, []);

  const loadAllContent = async () => {
    try {
      const [contentResponse, teamResponse, projectsResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/content/landing-page`),
        axios.get(`${BACKEND_URL}/api/team`),
        axios.get(`${BACKEND_URL}/api/projects`)
      ]);
      setContent(contentResponse.data);
      setTeamMembers(teamResponse.data);
      setProjects(projectsResponse.data);
    } catch (error) {
      console.error('Failed to load content:', error);
    } finally {
      setLoading(false);
    }
  };

  const sc = content?.studio_content;
  const buildingImage = sc?.building_image || '';
  const galleryImages = (sc?.gallery_images || []).filter(Boolean);
  const aboutText = sc?.about_text || '';
  const philosophyText = sc?.philosophy_text || '';
  const h = sc?.section_headings || {};
  const storyHeading = h.story_heading ?? 'our story';
  const galleryHeading = h.gallery_heading ?? '';
  const philosophyHeading = h.philosophy_heading ?? 'what guides us';
  const teamHeading = h.team_heading ?? 'the team';

  const aboutParagraphs = aboutText
    ? aboutText.split(/\n\n|\n/).filter(Boolean)
    : FALLBACK_ABOUT;
  const hasMultipleParagraphs = aboutParagraphs.length > 1;

  const getProjectsByIds = (projectIds) => {
    if (!projectIds || projectIds.length === 0) return [];
    return projects.filter(p => projectIds.includes(p.id));
  };

  const TeamMemberCard = ({ member, index }) => {
    const [isHovered, setIsHovered] = useState(false);
    const hasPhoto = !!member.photo;
    const hasSketch = !!member.sketch_photo;
    const colorClass = MEMBER_COLORS[index % MEMBER_COLORS.length];

    return (
      <div className="group cursor-pointer" data-testid={`team-member-${member.id}`}
        onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}
        onClick={() => setSelectedMember(member)}>
        <div className="relative aspect-square overflow-hidden bg-gray-100 mb-4">
          {hasSketch || hasPhoto ? (
            <>
              {hasSketch && (
                <img src={member.sketch_photo} alt={member.name} loading="lazy"
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${isHovered ? 'opacity-0' : 'opacity-100'}`} />
              )}
              {hasPhoto && (
                <img src={member.photo} alt={member.name} loading="lazy"
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${isHovered || !hasSketch ? 'opacity-100' : 'opacity-0'}`} />
              )}
            </>
          ) : (
            <div className={`w-full h-full ${colorClass} flex items-center justify-center`}>
              <span className="text-4xl font-bold opacity-60" style={{ fontFamily: 'Inter, sans-serif' }}>{getInitials(member.name)}</span>
            </div>
          )}
          <div className="absolute inset-0 bg-[#002F6C] bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-500"></div>
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
            <span className="bg-white/90 px-3 py-1.5 rounded text-xs lowercase" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>view profile</span>
          </div>
        </div>
        <div>
          <h3 className="text-sm text-gray-900 lowercase mb-1 group-hover:text-[#002F6C] transition-colors"
            style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700 }} data-testid={`team-name-${member.id}`}>{member.name}</h3>
          <p className="text-xs text-gray-500 lowercase" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 500 }}>{member.designation}</p>
        </div>
      </div>
    );
  };

  const MemberDetailModal = ({ member, onClose }) => {
    const memberProjects = getProjectsByIds(member.projects_worked || []);
    const hasPhoto = !!member.photo;
    const colorClass = MEMBER_COLORS[teamMembers.indexOf(member) % MEMBER_COLORS.length];

    return (
      <div className="fixed inset-0 bg-[#002F6C]/85 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="member-modal">
        <div className="bg-[#F8F8F7] rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <button onClick={onClose} className="fixed top-4 right-4 p-2 bg-[#F8F8F7] rounded-full shadow-lg hover:bg-gray-100 transition-colors z-10" data-testid="close-modal">
            <X size={24} />
          </button>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-8">
            <div className="aspect-[3/4] overflow-hidden rounded-lg">
              {hasPhoto ? (
                <img src={member.photo} alt={member.name} className="w-full h-full object-cover" />
              ) : (
                <div className={`w-full h-full ${colorClass} flex items-center justify-center`}>
                  <span className="text-7xl font-bold opacity-40" style={{ fontFamily: 'Inter, sans-serif' }}>{getInitials(member.name)}</span>
                </div>
              )}
            </div>
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl lowercase mb-2" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, color: '#002F6C' }}>{member.name}</h2>
                <p className="text-lg lowercase text-gray-700" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 500 }}>{member.designation}</p>
                {member.years_in_studio && (
                  <p className="text-sm text-gray-600 mt-2" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 500 }}>{member.years_in_studio}</p>
                )}
              </div>
              {member.bio && (
                <div>
                  <h3 className="text-sm uppercase tracking-wide mb-3" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, color: '#002F6C' }}>About</h3>
                  <p className="text-sm text-gray-700 leading-relaxed" style={{ fontFamily: 'Lato, sans-serif', fontWeight: 300, lineHeight: '1.7' }}>{member.bio}</p>
                </div>
              )}
              <div className="flex gap-4">
                {member.email && (
                  <a href={`mailto:${member.email}`} className="flex items-center gap-2 text-sm hover:text-[#002F6C] transition-colors" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 400 }}>
                    <Mail size={18} /><span className="lowercase">email</span>
                  </a>
                )}
                {member.linkedin && (
                  <a href={member.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm hover:text-[#002F6C] transition-colors" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 400 }}>
                    <Linkedin size={18} /><span className="lowercase">linkedin</span>
                  </a>
                )}
              </div>
              {memberProjects.length > 0 && (
                <div>
                  <h3 className="text-sm uppercase tracking-wide mb-3" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, color: '#002F6C' }}>Projects ({memberProjects.length})</h3>
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {memberProjects.map((project) => (
                      <Link key={project.id} to={`/work/${project.id}`} onClick={onClose} className="group block p-3 border rounded hover:border-[#002F6C] transition-all">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="text-sm lowercase mb-1 group-hover:text-[#002F6C] transition-colors" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>{project.title}</h4>
                            <p className="text-xs text-gray-500 lowercase" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 500 }}>{project.location} {project.year && `\u2022 ${project.year}`}</p>
                          </div>
                          <ExternalLink size={16} className="text-gray-400 group-hover:text-[#002F6C] transition-colors" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="studio-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#002F6C]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white overflow-x-hidden" data-testid="studio-page">
      <Seo
        title="studio — studio 2+2, Ahmedabad"
        description="studio 2+2 is a landscape architecture practice in Ahmedabad, India. 1000+ projects over nine years. taking care of your natural affairs."
      />

      {/* ── Hero: full-bleed building image with story overlay ── */}
      <section
        className="relative w-screen left-1/2 -translate-x-1/2 flex items-center"
        data-testid="studio-hero"
        style={{
          height: '85vh',
          backgroundImage: buildingImage ? `url(${buildingImage})` : 'none',
          backgroundColor: buildingImage ? undefined : '#0a1432',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
        }}
      >
        {/* Gradient overlay */}
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(to right, rgba(10,20,50,0.75) 0%, rgba(10,20,50,0.4) 60%, rgba(10,20,50,0.0) 100%)'
        }} />

        {/* Text content — left half, vertically centred */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="md:w-1/2">
            {storyHeading && (
              <h2
                className="text-xs uppercase tracking-[0.25em] text-white/70 mb-6"
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
                data-testid="story-heading"
              >
                {storyHeading}
              </h2>
            )}

            {/* First paragraph — always visible */}
            <p
              className="text-white lowercase mb-6"
              style={{ fontFamily: 'Lato, sans-serif', fontWeight: 300, fontSize: '1.1rem', lineHeight: 1.8 }}
            >
              {aboutParagraphs[0]}
            </p>

            {/* Remaining paragraphs — collapsible */}
            {hasMultipleParagraphs && (
              <>
                <div
                  className="overflow-hidden transition-[max-height] duration-500 ease-in-out"
                  style={{ maxHeight: aboutExpanded ? '1000px' : '0px' }}
                >
                  <div className="space-y-5 pb-2">
                    {aboutParagraphs.slice(1).map((para, i) => (
                      <p key={i} className="text-white/85 lowercase"
                        style={{ fontFamily: 'Lato, sans-serif', fontWeight: 300, fontSize: '1rem', lineHeight: 1.8 }}>
                        {para}
                      </p>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => setAboutExpanded(prev => !prev)}
                  className="mt-2 text-white/80 hover:text-white text-sm lowercase transition-colors"
                  style={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, borderBottom: '1px solid rgba(255,255,255,0.4)' }}
                  data-testid="read-more-toggle"
                >
                  {aboutExpanded ? 'read less \u2191' : 'read more \u2193'}
                </button>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ── Gallery Slideshow — only rendered when images exist ── */}
      {galleryImages.length > 0 && (
        <GallerySlideshow images={galleryImages} heading={galleryHeading} />
      )}

      {/* Values / Philosophy Section */}
      <section className="py-24 px-4 md:px-8 lg:px-12 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          {philosophyHeading && (
            <h2 className="text-2xl text-[#002F6C] lowercase mb-16 text-center" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700 }}>
              {philosophyHeading}
            </h2>
          )}
          {philosophyText ? (
            <div className="max-w-3xl mx-auto space-y-6 text-gray-600 leading-relaxed lowercase text-center" style={{ fontFamily: 'Lato, sans-serif', fontWeight: 300 }}>
              {philosophyText.split('\n').filter(Boolean).map((para, i) => <p key={i}>{para}</p>)}
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-12">
              {FALLBACK_VALUES.map((value, index) => (
                <div key={index} className="text-center">
                  <h3 className="text-xl text-gray-900 lowercase mb-2" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700 }}>{value.title}</h3>
                  <p className="text-sm text-[#002F6C] uppercase tracking-widest mb-4" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>{value.subtitle}</p>
                  <p className="text-gray-600 lowercase leading-relaxed" style={{ fontFamily: 'Lato, sans-serif', fontWeight: 300 }}>{value.description}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 px-4 md:px-8 lg:px-12" data-testid="team-section">
        <div className="max-w-5xl mx-auto">
          {teamHeading && (
            <h2 className="text-2xl text-[#002F6C] lowercase mb-16" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700 }}>{teamHeading}</h2>
          )}
          {teamMembers.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8" data-testid="team-grid">
              {teamMembers.map((member, index) => (
                <TeamMemberCard key={member.id} member={member} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 lowercase" style={{ fontFamily: 'Lato, sans-serif', fontWeight: 300 }}>team members can be added via the admin panel.</p>
            </div>
          )}
        </div>
      </section>

      {selectedMember && <MemberDetailModal member={selectedMember} onClose={() => setSelectedMember(null)} />}

      {/* CTA Section */}
      <section className="py-24 px-4 md:px-8 lg:px-12 bg-[#002F6C]" data-cursor-invert>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl text-white lowercase mb-6" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700 }}>let us create something together</h2>
          <p className="text-white/70 lowercase mb-8" style={{ fontFamily: 'Lato, sans-serif', fontWeight: 300 }}>
            whether you have a project in mind or simply want to explore possibilities, we would love to hear from you.
          </p>
          <a href="/contact" className="inline-block bg-white text-[#002F6C] px-8 py-3 hover:bg-gray-100 transition-colors lowercase text-sm"
            style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700 }}>get in touch</a>
        </div>
      </section>
    </div>
  );
};

export default Studio;
